import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useContact } from "@/context/ContactContext";
import { useProductDetail } from "@/context/ProductDetailContext";
import { useClient } from "@/context/ClientContext";
import { pText } from "@/data/productDe";

export default function ProductCard({ product, index, query = "" }) {
  const { lang, t } = useLang();
  const { openContact } = useContact();
  const { openDetail } = useProductDetail();
  const { client } = useClient();
  const norm = (s) => s.toLowerCase().replace(/[\s\-/.]/g, "");
  const matches = (oem) => query && norm(oem).includes(query);
  const name = pText(product, lang, "name");

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.id}`}
      className="product-card group relative flex flex-col border border-white/10 bg-[#112318] overflow-hidden"
    >
      <div
        className="relative aspect-[4/3] overflow-hidden cursor-pointer"
        onClick={() => openDetail(product)}
        data-testid={`open-detail-${product.id}`}
      >
        <img
          src={product.image}
          alt={name}
          className="h-full w-full object-cover grayscale-[0.35] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#112318] via-transparent to-transparent opacity-80" />
        <div className="product-spotlight absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <span className="absolute top-3 left-3 font-mono text-[11px] tracking-widest text-black bg-[color:var(--dp-orange)] px-2 py-1">
          {t.catalog.ref} {product.ref}
        </span>
        <span className="absolute bottom-3 right-3 font-mono text-[10px] uppercase tracking-widest text-white bg-black/60 backdrop-blur px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {t.catalog.viewDetails} →
        </span>
      </div>

      <div className="flex flex-1 flex-col p-6">
        <div className="flex flex-wrap gap-2 mb-4">
          {product.specs.map((s) => (
            <span
              key={s}
              className="font-mono text-[10px] uppercase tracking-widest text-white/55 border border-white/12 px-2 py-1"
            >
              {s}
            </span>
          ))}
        </div>
        <h3
          className="font-heading text-lg font-bold text-white leading-tight cursor-pointer hover:text-[color:var(--dp-orange)] transition-colors"
          onClick={() => openDetail(product)}
        >
          {name}
        </h3>
        <p className="mt-3 text-sm text-white/60 font-light leading-relaxed flex-1">
          {pText(product, lang, "desc")}
        </p>

        {product.oem && product.oem.length > 0 && (
          <div className="mt-5">
            <span className="block font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
              {t.catalog.oem}
            </span>
            <div className="flex flex-wrap gap-2" data-testid={`oem-list-${product.id}`}>
              {product.oem.map((code) => (
                <span
                  key={code}
                  data-testid={`oem-${code}`}
                  className={`font-mono text-[11px] px-2 py-1 border transition-colors ${
                    matches(code)
                      ? "bg-[color:var(--dp-orange)] text-black border-[color:var(--dp-orange)]"
                      : "text-white/60 border-white/12"
                  }`}
                >
                  {code}
                </span>
              ))}
            </div>
          </div>
        )}

        <button
          data-testid={`inquire-${product.id}`}
          onClick={() =>
            client
              ? openDetail(product)
              : openContact({ ref: product.ref, name, id: product.id })
          }
          className={`mt-6 inline-flex items-center justify-between gap-2 w-full px-5 py-3 border font-semibold text-sm transition-colors group/btn ${
            client
              ? "bg-[color:var(--dp-orange)] hover:bg-[color:var(--dp-orange-hover)] border-[color:var(--dp-orange)] text-black"
              : "bg-white/5 hover:bg-[color:var(--dp-orange)] border-white/12 hover:border-[color:var(--dp-orange)] text-white hover:text-black"
          }`}
        >
          {client ? t.catalog.order : t.catalog.inquire}
          <ArrowUpRight size={18} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
        </button>
      </div>
    </motion.article>
  );
}
